import { format } from 'date-fns';
import { Sparkles, ListOrdered, History, FileUp, ChevronRight, Table2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { WizardBar, WizardHead } from './WizardChrome';

// The Game Planner's front door. Four ways to build a plan, plus availability.
//
// Availability sits ABOVE the four options on purpose: it is a precondition, not
// a decision, and putting it first means all four paths respect it. Without this
// Auto-generate would happily field a kid who is not at the park.

const EntryCard = ({ icon, title, desc, onClick, tone }: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  onClick?: () => void;
  tone?: 'gold';
}) => (
  <button
    onClick={onClick}
    className="w-full bg-surface rounded-panel px-4 py-4 flex items-center gap-3.5 text-left mb-3"
  >
    <span className={`w-[34px] h-[34px] shrink-0 rounded-control flex items-center justify-center ${tone === 'gold' ? 'bg-gold-soft text-gold-ink' : 'bg-raised text-ink'}`}>
      {icon}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-[16.5px] font-bold tracking-[-0.018em] text-ink">{title}</span>
      <span className="block text-[13.5px] text-ink-2 mt-0.5 leading-snug">{desc}</span>
    </span>
    <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
  </button>
);

export const PlanEntry = ({
  game, team, presentCount, absentCount, lastPlannedGame,
  onAvailability, onAutoGenerate, onStepByStep, onUseLast, onImport, onManual, isImporting, onClose,
}: {
  game: any;
  team: any;
  presentCount: number;
  absentCount: number;
  lastPlannedGame: any | null;
  onAvailability: () => void;
  onAutoGenerate: () => void;
  onStepByStep: () => void;
  onUseLast: () => void;
  onImport: (file: File) => void;
  onManual: () => void;
  isImporting: boolean;
  onClose: () => void;
}) => {
  const d = new Date(game.date);
  const meta = [
    format(d, 'MMM d'),
    game.time || null,
    game.isHome ? 'Home' : 'Away',
    `${game.plan?.numInnings || team.defaultInnings || 6} innings`,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <WizardBar onClose={onClose} />
      <WizardHead
        title={`${format(d, 'EEE')} ${game.isHome ? 'vs' : 'at'} ${game.opponent || 'Opponent'}`}
        sub={meta}
      />

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
        {/* Availability. A precondition for all four paths below. */}
        <button
          onClick={onAvailability}
          className="w-full bg-surface rounded-card px-4 py-3.5 flex items-center gap-3 mb-5 text-left"
        >
          <span className="flex-1 text-[15.5px] font-semibold text-ink">Availability</span>
          <span className="text-[14.5px] text-ink-2 tabular-nums">
            {presentCount} in{absentCount > 0 && <>, <span className="text-danger font-bold">{absentCount} out</span></>}
          </span>
          <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
        </button>

        <EntryCard
          tone="gold"
          icon={<Sparkles className="w-[19px] h-[19px]" />}
          title="Auto-generate Lineup"
          desc="Dugout suggests positions and order based on season stats"
          onClick={onAutoGenerate}
        />
        <EntryCard
          icon={<ListOrdered className="w-[19px] h-[19px]" />}
          title="Step by step"
          desc="Decide each one yourself"
          onClick={onStepByStep}
        />
        {lastPlannedGame && (
          <EntryCard
            icon={<History className="w-[19px] h-[19px]" />}
            title="Use last game"
            desc={`${lastPlannedGame.isHome ? 'vs' : 'at'} ${lastPlannedGame.opponent || 'Opponent'}, ${format(new Date(lastPlannedGame.date), 'MMM d')}`}
            onClick={onUseLast}
          />
        )}

        {/* Import stays web-only, matching the existing header button: it is a
            file picker and remains unverified on device. */}
        {!Capacitor.isNativePlatform() && (
          <label className="w-full bg-surface rounded-panel px-4 py-4 flex items-center gap-3.5 text-left mb-3 cursor-pointer">
            <span className="w-[34px] h-[34px] shrink-0 rounded-control flex items-center justify-center bg-raised text-ink">
              <FileUp className="w-[19px] h-[19px]" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[16.5px] font-bold tracking-[-0.018em] text-ink">
                {isImporting ? 'Reading card…' : 'Import GameChanger lineup'}
              </span>
              <span className="block text-[13.5px] text-ink-2 mt-0.5 leading-snug">Read a lineup card PDF</span>
            </span>
            <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
            <input
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              disabled={isImporting}
              onChange={e => { const f = e.target.files?.[0]; if (f) onImport(f); e.target.value = ''; }}
            />
          </label>
        )}

        {/* For coaches who already know their plan: everything on one screen,
            the way the planner worked before the wizard. Set apart from the
            four guided paths because it skips the guidance. */}
        <button
          onClick={onManual}
          className="w-full mt-3 rounded-panel border border-dashed border-hairline px-4 py-4 flex items-center gap-3.5 text-left"
        >
          <span className="w-[34px] h-[34px] shrink-0 rounded-control flex items-center justify-center bg-surface text-ink">
            <Table2 className="w-[19px] h-[19px]" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[16.5px] font-bold tracking-[-0.018em] text-ink">Skip Wizard, Manually Plan</span>
            <span className="block text-[13.5px] text-ink-2 mt-0.5 leading-snug">
              Pitcher, batting order and every inning on one screen
            </span>
          </span>
          <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
        </button>

        <div className="h-4" />
      </div>
    </>
  );
};
